import React, { useState, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import GenericModal from '../../../components/modal/GenericModal';
import InputText from '../../../components/modalComponents/InputText';
import SpinnerLoading from '../../../components/pageComponents/SpinnerLoading';
import { ESTADOS_FIRMA_TP } from '../../../constants/hardCodedConstants';
import { useGetRequest } from '../../../hooks/useGetRequest/useGetRequest';
import { useManagePostRequest } from '../../../hooks/useManagePostRequest/useManagePostRequest';
import {
    getFichaFirmaAPI,
    getHistorialFirmaAPI,
    descargarDocumentoFirmadoAPI,
    observarFirmaAPI,
    aprobarFirmaAPI,
} from '../../../consumers/backendApisUrls';

// ──────────────────────────────────────────────────────────────────────────
// HU-TR-07 CA-10 a CA-16, CA-20 y CA-21: "[Campaña] – Revisión firma colaborador".
//
// GDH revisa el documento que el colaborador firmó y decide (CA-14):
//   Observar             → vuelve al colaborador con un motivo obligatorio (R-09)
//   Guardar y finalizar  → conformidad, cierre del proceso y de la vacante (CA-23)
//
// El historial de ciclos (CA-20) va como acordeón: en la primera vuelta no hay
// nada que mostrar, y a partir de la segunda es donde se ve qué se observó antes
// y qué se subsanó.
// ──────────────────────────────────────────────────────────────────────────

const RevisionFirmaGDH = ({ toggleModal, refreshPage, postulante = {} }) => {
    const idPostulante = postulante?.idPostulante;

    const [executeGet] = useGetRequest();
    const [managePost] = useManagePostRequest();

    const [ficha, setFicha] = useState(null);
    const [historial, setHistorial] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSending, setIsSending] = useState(false);
    const [verHistorial, setVerHistorial] = useState(false);

    // CA-15: el campo de motivo aparece recién al elegir "Observar", para no
    // sugerir que hay que llenarlo cuando la decisión puede ser aprobar.
    const [mostrarObservacion, setMostrarObservacion] = useState(false);
    const [comentario, setComentario] = useState('');
    const [descargando, setDescargando] = useState(false);

    useEffect(() => {
        if (!idPostulante) return;
        let cancelado = false;
        (async () => {
            const [fichaResp, historialResp] = await Promise.all([
                executeGet(getFichaFirmaAPI(idPostulante)),
                executeGet(getHistorialFirmaAPI(idPostulante)),
            ]);
            if (cancelado) return;
            setFicha(fichaResp?.data ?? null);
            setHistorial(historialResp?.data ?? []);
            setIsLoading(false);
        })();
        return () => { cancelado = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idPostulante]);

    // CA-13: el documento se resuelve en el backend a partir del postulante; acá
    // solo se abre la URL prefirmada que devuelve.
    //
    // Misma pestaña síncrona + "Generando documento..." que FirmaColaborador.jsx
    // (23/09/2026): abrir la pestaña después del await deja que el navegador la
    // bloquee como popup no solicitado.
    const handleDescargar = async () => {
        const previewWindow = window.open('', '_blank');
        if (!previewWindow) {
            toast.warn('Permita ventanas emergentes para ver el documento.');
            return;
        }
        previewWindow.document.write('<p style="font-family:sans-serif;text-align:center;margin-top:40px;color:#666;">Generando documento...</p>');
        setDescargando(true);
        try {
            const respuesta = await executeGet(descargarDocumentoFirmadoAPI(idPostulante));
            const url = respuesta?.data?.UrlS3;
            if (!url) {
                toast.error(respuesta?.data?.Message ?? 'No se pudo obtener el documento firmado.');
                previewWindow.close();
                return;
            }
            previewWindow.location.href = url;
        } catch {
            previewWindow.close();
        } finally {
            setDescargando(false);
        }
    };

    // CA-15 / CA-16 / CA-20: dos clics a propósito. El primero abre el campo, el
    // segundo confirma — el motivo es obligatorio y devolver la tarea sin querer
    // le cuesta al colaborador otra vuelta completa.
    const handleObservar = async () => {
        if (!mostrarObservacion) {
            setMostrarObservacion(true);
            return;
        }
        if (!comentario.trim()) {
            toast.warn('Indique el motivo de la observación.');
            return;
        }
        setIsSending(true);
        try {
            await managePost(
                observarFirmaAPI(),
                { IdPostulante: idPostulante, Comentario: comentario.trim() },
                async () => {
                    toast.success('Observación enviada al colaborador para su subsanación.');
                    refreshPage && refreshPage();
                    toggleModal();
                },
                false
            );
        } finally {
            setIsSending(false);
        }
    };

    // CA-21 / CA-23: cierra el proceso y ocupa la vacante. Es irreversible, por eso
    // el texto del botón dice lo que hace y no un genérico "Guardar".
    const handleAprobar = async () => {
        setIsSending(true);
        try {
            await managePost(
                aprobarFirmaAPI(),
                { IdPostulante: idPostulante },
                async () => {
                    toast.success(`${ficha?.TipoProceso === 'P' ? 'Promoción' : 'Traslado'} cerrado correctamente.`);
                    refreshPage && refreshPage();
                    toggleModal();
                },
                false
            );
        } finally {
            setIsSending(false);
        }
    };

    const titulo = ficha?.NombreConvocatoria
        ? `${ficha.NombreConvocatoria} – Revisión firma colaborador`
        : 'Revisión firma colaborador';

    if (isLoading) {
        return (
            <GenericModal showModal={toggleModal} titulo="Revisión firma colaborador" width="950px">
                <div className="modal-body text-center py-5"><SpinnerLoading /></div>
            </GenericModal>
        );
    }

    if (!ficha) {
        return (
            <GenericModal showModal={toggleModal} titulo="Revisión firma colaborador" width="950px">
                <div className="modal-body">
                    <AvisoBox>No se pudo cargar la información del proceso.</AvisoBox>
                </div>
            </GenericModal>
        );
    }

    const cerrado = ficha.IdEstadoCiclo === ESTADOS_FIRMA_TP.CERRADO;
    // HU-PR-09: mismo criterio que FirmaColaborador.jsx.
    const esPromocion = ficha.TipoProceso === 'P';

    return (
        <GenericModal showModal={toggleModal} titulo={titulo} width="950px">
            <div className="modal-body pt-3">

                {/* CA-11 */}
                <SubTitulo>Datos del colaborador</SubTitulo>
                <div className="displayFlexWrap">
                    <div className="col-md-6"><InputText label="Nombre" name="nombre" value={ficha.NombreColaborador} direction="row" isDisabled /></div>
                    <div className="col-md-6"><InputText label="DNI" name="dni" value={ficha.Dni} direction="row" isDisabled /></div>
                    <div className="col-md-6"><InputText label="Puesto actual" name="puesto" value={ficha.PuestoActual} direction="row" isDisabled /></div>
                    <div className="col-md-6"><InputText label="Especialidad" name="especialidad" value={ficha.Especialidad ?? '—'} direction="row" isDisabled /></div>
                    <div className="col-md-6"><InputText label="Sede actual" name="sedeActual" value={ficha.SedeActual} direction="row" isDisabled /></div>
                    <div className="col-md-6"><InputText label="Región" name="region" value={ficha.RegionActual ?? '—'} direction="row" isDisabled /></div>
                    <div className="col-md-6"><InputText label="Fecha de ingreso" name="fechaIngreso" value={ficha.FechaIngreso} direction="row" isDisabled /></div>
                </div>

                <hr />

                {/* CA-12 */}
                <SubTitulo>{esPromocion ? 'Condiciones de la promoción' : 'Condiciones del traslado'}</SubTitulo>
                <div className="displayFlexWrap">
                    <div className="col-md-6"><InputText label="Proceso" name="proceso" value={ficha.NombreConvocatoria} direction="row" isDisabled /></div>
                    <div className="col-md-6"><InputText label="Tipo" name="tipo" value={esPromocion ? 'Promoción' : 'Traslado'} direction="row" isDisabled /></div>
                    <div className="col-md-6"><InputText label="Puesto de destino" name="puestoDestino" value={ficha.PuestoDestino ?? '—'} direction="row" isDisabled /></div>
                    <div className="col-md-6"><InputText label="Sede de destino" name="sedeDestino" value={ficha.SedeDestino ?? '—'} direction="row" isDisabled /></div>
                    <div className="col-md-6"><InputText label="Headcount" name="headcount" value={ficha.Headcount ?? '—'} direction="row" isDisabled /></div>
                    <div className="col-md-6"><InputText label="Fecha efectiva" name="fechaEfectiva" value={ficha.FechaEfectiva} direction="row" isDisabled /></div>
                    {/* HU-PR-09 CA-11: solo aplica a promoción. */}
                    {esPromocion && (
                        <>
                            <div className="col-md-6"><InputText label="Sueldo de promoción" name="sueldoPromocion" value={ficha.SueldoPromocion ?? '—'} direction="row" isDisabled /></div>
                            <div className="col-md-6"><InputText label="Especialidad de destino" name="especialidadRequerimiento" value={ficha.EspecialidadRequerimiento ?? '—'} direction="row" isDisabled /></div>
                        </>
                    )}
                </div>

                <hr />

                <div className="displayFlexWrap">
                    <div className="col-md-4"><InputText label="Intento" name="ciclo" value={`N° ${ficha.NroCiclo ?? '—'}`} direction="row" isDisabled /></div>
                    <div className="col-md-4"><InputText label="Firmado el" name="fechaEnvio" value={ficha.FechaEnvioColaborador || '—'} direction="row" isDisabled /></div>
                    <div className="col-md-4"><InputText label="Revisado el" name="fechaRevision" value={ficha.FechaRevisionGdh || '—'} direction="row" isDisabled /></div>
                </div>

                {/* CA-13 */}
                {ficha.TieneDocumento && (
                    <div className="text-center my-3">
                        <button type="button" className="btn generic-button-buscador"
                            disabled={descargando} onClick={handleDescargar}>
                            <FontAwesomeIcon icon="file-pdf" className="mr-2" />
                            Descargar documento firmado
                            {descargando && <FontAwesomeIcon icon="spinner" className="spinner ml-2" />}
                        </button>
                    </div>
                )}

                {/* CA-20: el historial solo aporta a partir del segundo ciclo. */}
                {historial.length > 1 && (
                    <HistorialBox className="mb-3">
                        <button type="button" className="btn btn-link p-0"
                            onClick={() => setVerHistorial(!verHistorial)}>
                            <FontAwesomeIcon icon={verHistorial ? 'chevron-down' : 'chevron-right'} className="mr-2" />
                            Historial de observaciones ({historial.length} intentos)
                        </button>

                        {verHistorial && (
                            <table className="table table-sm mt-2 mb-0">
                                <thead>
                                    <tr>
                                        <th style={{ width: '60px' }}>Intento</th>
                                        <th style={{ width: '110px' }}>Estado</th>
                                        <th>Motivo de la observación</th>
                                        <th style={{ width: '150px' }}>Revisó</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {historial.map((ciclo) => (
                                        <tr key={ciclo.IdFirmaCiclo}>
                                            <td>N° {ciclo.NroCiclo}</td>
                                            <td>{ciclo.DesEstadoCiclo ?? '—'}</td>
                                            <td>{ciclo.ComentarioObservacion || '—'}</td>
                                            <td>
                                                {ciclo.NombreGdh ?? '—'}
                                                {ciclo.FechaRevisionGdh && <small className="d-block text-muted">{ciclo.FechaRevisionGdh}</small>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </HistorialBox>
                )}

                {/* CA-15 */}
                {mostrarObservacion && (
                    <div className="mb-3">
                        <label className="modal-label required">Motivo de la observación</label>
                        <textarea className="form-control" rows="3" value={comentario}
                            onChange={(e) => setComentario(e.target.value)}
                            placeholder="Indique qué debe corregir, aclarar o completar el colaborador" />
                    </div>
                )}

                {/* CA-14 */}
                {ficha.PuedeRevisar ? (
                    <div className="d-flex justify-content-center mt-4" style={{ gap: '12px' }}>
                        <button type="button" className="btn btn-warning"
                            disabled={isSending} onClick={handleObservar}>
                            {mostrarObservacion ? 'Confirmar observación' : 'Observar'}
                        </button>
                        {!mostrarObservacion && (
                            <button type="button" className="btn generic-button-buscador generic-button-hover-verde green-innova-background"
                                disabled={isSending} onClick={handleAprobar}>
                                Guardar y finalizar
                                {isSending && <FontAwesomeIcon icon="spinner" className="spinner ml-2" />}
                            </button>
                        )}
                    </div>
                ) : (
                    <AvisoBox className="mt-3">
                        {cerrado
                            ? `Este ${esPromocion ? 'proceso de promoción' : 'traslado'} ya fue aprobado y cerrado. La vacante quedó asignada.`
                            : ficha.EstaObservado
                                ? 'El documento fue observado y está pendiente de subsanación por el colaborador.'
                                : 'El colaborador todavía no envió el documento firmado.'}
                    </AvisoBox>
                )}
            </div>
        </GenericModal>
    )
}

export default RevisionFirmaGDH

const SubTitulo = styled.div`
    font-weight: bold;
    color: #0b2b57;
    margin-bottom: 10px;
`;

const AvisoBox = styled.div`
    border: 1px solid #ffe08a;
    background: #fff9e6;
    border-radius: 8px;
    padding: 14px;
    color: #8a6d3b;
    font-size: 14px;
`;

const HistorialBox = styled.div`
    border: 1px solid #dee2e6;
    border-radius: 8px;
    padding: 12px;
    background: #f8f9fa;
`;
